import { Easing, Platform, StyleProp, View, ViewStyle } from 'react-native'
import Animated, { FadeInDown, FadeOutUp } from 'react-native-reanimated'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import cn from '@ui/utils/cn'
import { ButtonProps } from '@ui/buttons/Button/types'
import useRWD from '@ui/theme/useRWD'
import { buttonsFixed, gap } from '@ui/theme/values'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import ButtonsGrid from './ButtonsGrid'

type Props = {
  children?: React.ReactNode
  buttons?: ButtonProps[]
  height?: number
  hidden?: boolean
  style?: StyleProp<ViewStyle>
  styleGradient?: StyleProp<ViewStyle>
  disableTransitions?: boolean
}

const HEIGHT = buttonsFixed.height
const isWeb = Platform.OS === 'web'

export const SpaceButtons = ({ height = HEIGHT }: { height?: number }) => (
  <View className={cn('pointer-events-none w-full')} style={{ height }} />
)

const FixedGradient = ({
  styleGradient,
  height = HEIGHT,
}: Pick<Props, 'styleGradient' | 'height'>) => (
  <View
    className={cn('pointer-events-none absolute bottom-0 left-0 w-full')}
    style={[{ height }, styleGradient]}
  >
    <Gradient
      colors={[colors.white0, colors.white, colors.white]}
      start={[0.5, 0]}
      end={[0.5, 1]}
      style={{ height }}
    />
  </View>
)

function Buttons({
  buttons,
  children,
  hidden,
  style = {},
  disableTransitions = false,
}: Omit<Props, 'styleGradient' | 'height'>) {
  const { screen } = useRWD()

  const styleOpacity = useAnimatedOpacity(!hidden)

  const isTransitions = isWeb && !disableTransitions

  return (
    <Animated.View
      className={cn(
        'absolute flex w-full flex-row items-center justify-center'
      )}
      entering={
        isTransitions
          ? FadeInDown.duration(300).easing(Easing.out(Easing.quad))
          : undefined
      }
      exiting={isTransitions ? FadeOutUp.duration(500) : undefined}
      style={[
        {
          bottom: 2 * gap, // gap,
          left: gap,
          gap: 2 * gap,
          width: screen.width - 2 * gap,
          paddingVertical: 0, // gap,,
          paddingHorizontal: gap,
        },
        styleOpacity,
        style,
      ]}
    >
      {children}
      <ButtonsGrid buttons={buttons ?? []} />
    </Animated.View>
  )
}

const ButtonsFixed = ({ styleGradient, height = HEIGHT, ...props }: Props) => {
  return (
    <>
      <FixedGradient styleGradient={styleGradient} height={height} />
      <Buttons {...props} />
    </>
  )
}

ButtonsFixed.Buttons = Buttons
ButtonsFixed.Gradient = FixedGradient

export default ButtonsFixed
